import React, { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import { Card, Chip, IconButton, Tooltip, toast } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Edit04Icon,
  InformationCircleIcon,
  RefreshIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import { toastMutationError } from "utils/toastMutationError";
import {
  IssuerCardProduct,
  IssuerCardProductUpdate,
  PlatformIntegration,
} from "hooks/usePlatformIntegrations";
import { CARD_HOLDER_LABEL_KEY } from "utils/platformIntegrations/integrationLabels";
import { Inline, Stack } from "components/layout/reusableLayouts";
import IntegrationCardProgramDrawer from "./IntegrationCardProgramDrawer";

interface IntegrationCardProgramsProps {
  integration: PlatformIntegration;
  /** The issuer's brand name, for the refresh. */
  providerName: string;
  refreshing: boolean;
  onRefresh: () => Promise<void>;
  onUpdate: (
    productKey: string,
    input: IssuerCardProductUpdate,
  ) => Promise<void>;
}

/**
 * The card programs one card issuing integration offers. One switch offers a
 * program to every company on the platform and another makes its purchases
 * earn rewards; its name and who it may issue cards to are edited in a drawer.
 */
const IntegrationCardPrograms: React.FC<IntegrationCardProgramsProps> = ({
  integration,
  providerName: provider,
  refreshing,
  onRefresh,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const editing =
    integration.cardProducts.find((product) => product.key === editingKey) ??
    null;
  // A lone program is edited from the card's header; with several, each row carries its own Edit.
  const onlyProduct =
    integration.cardProducts.length === 1
      ? integration.cardProducts[0]
      : undefined;

  const toggle = async (
    product: IssuerCardProduct,
    field: "enabled" | "rewards",
  ) => {
    try {
      await onUpdate(product.key, { [field]: !product[field] });
    } catch (error: unknown) {
      toastMutationError(error, t("Please try again"));
    }
  };

  const refresh = async () => {
    try {
      await onRefresh();
      toast.success(t("CardProductsRefreshedToast", { provider }));
    } catch (error: unknown) {
      toastMutationError(error, t("Please try again"));
    }
  };

  return (
    <Card
      variant="outlined"
      title={t("Card programs")}
      subtitle={t("IntegrationCardProgramsHelp")}
      action={{
        type: "content",
        content: (
          <Inline gap="var(--space-2)" wrap={false}>
            {onlyProduct && (
              <Button
                variant="default-outlined"
                size="small"
                startIcon={<HugeiconsIcon icon={Edit04Icon} size={16} />}
                onClick={() => setEditingKey(onlyProduct.key)}
              >
                {t("Edit")}
              </Button>
            )}
            <Tooltip
              describeChild
              title={t("RefreshFromProvider", { provider })}
            >
              <IconButton
                size="small"
                variant="outlined"
                aria-label={t("RefreshFromProvider", { provider })}
                disabled={refreshing}
                onClick={() => void refresh()}
              >
                <HugeiconsIcon icon={RefreshIcon} size={16} />
              </IconButton>
            </Tooltip>
          </Inline>
        ),
      }}
    >
      {integration.cardProducts.length === 0 ? (
        <Typography
          variant="body2"
          sx={{ color: "var(--color-text-tertiary)" }}
        >
          {t("NoIntegrationCardProgramsYet", { provider })}
        </Typography>
      ) : (
        <Stack gap="var(--space-4)">
          {integration.cardProducts.map((product, index) => (
            <Fragment key={product.key}>
              {index > 0 && <Divider />}
              <Inline justify="between" gap="var(--space-3)">
                <Stack
                  gap="var(--space-2)"
                  sx={{ flex: "1 1 16rem", minWidth: 0 }}
                >
                  <Typography variant="subtitle2">
                    {product.displayName}
                  </Typography>
                  <Inline gap="var(--space-2)">
                    {product.subAccountType && (
                      <Chip
                        size="sm"
                        tone="neutral"
                        label={t(product.subAccountType)}
                      />
                    )}
                    {product.cardHolderTypes.map((type) => (
                      <Chip
                        key={type}
                        size="sm"
                        tone="info"
                        label={t(CARD_HOLDER_LABEL_KEY[type])}
                      />
                    ))}
                  </Inline>
                </Stack>
                <Inline gap="var(--space-3)" wrap={false}>
                  {!onlyProduct && (
                    <Button
                      variant="default-outlined"
                      size="small"
                      startIcon={<HugeiconsIcon icon={Edit04Icon} size={16} />}
                      onClick={() => setEditingKey(product.key)}
                    >
                      {t("Edit")}
                    </Button>
                  )}
                  <FormControlLabel
                    label={
                      <Inline gap="var(--space-1)" wrap={false}>
                        {t("Rewards")}
                        <Tooltip title={t("CardProgramRewardsHelp")}>
                          <Box
                            component="span"
                            tabIndex={0}
                            aria-label={t("CardProgramRewardsHelp")}
                            sx={{
                              display: "inline-flex",
                              color: "var(--color-text-tertiary)",
                            }}
                          >
                            <HugeiconsIcon
                              icon={InformationCircleIcon}
                              size={14}
                            />
                          </Box>
                        </Tooltip>
                      </Inline>
                    }
                    control={
                      <Switch
                        checked={product.rewards}
                        onChange={() => void toggle(product, "rewards")}
                        slotProps={{
                          input: {
                            "aria-label": `${t("Rewards")}: ${product.displayName}`,
                          },
                        }}
                      />
                    }
                  />
                  <FormControlLabel
                    label={t("Enable")}
                    control={
                      <Switch
                        checked={product.enabled}
                        onChange={() => void toggle(product, "enabled")}
                        slotProps={{
                          input: {
                            "aria-label": `${t("Enable")}: ${product.displayName}`,
                          },
                        }}
                      />
                    }
                  />
                </Inline>
              </Inline>
            </Fragment>
          ))}
        </Stack>
      )}

      <IntegrationCardProgramDrawer
        open={!!editing}
        product={editing}
        providerName={provider}
        supportedCardHolderTypes={
          editing?.supportedCardHolderTypes ??
          integration.supportedCardHolderTypes
        }
        onClose={() => setEditingKey(null)}
        onSave={onUpdate}
      />
    </Card>
  );
};

export default IntegrationCardPrograms;
